import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/permissions";
import { Send, ArrowRightLeft, PackageCheck, FileDown, Wrench } from "lucide-react";
import { SingleMachinePicker } from "@/components/MachinePicker";

/**
 * Renders spare-issue UI on an Asset detail page.
 *  - If asset is a Spare/Jig: shows current deployment + "Issue to Machine" button + history.
 *  - If asset is any other asset: shows all spares currently issued to it.
 */
export default function SpareIssuePanel({ asset, canIssueSpare, onChanged }) {
  const [issues, setIssues] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issueOpen, setIssueOpen] = useState(false);
  const [returnIssue, setReturnIssue] = useState(null);
  const [busy, setBusy] = useState(false);

  const isSpare = asset?.category === "Attachments and Jigs";

  const load = async () => {
    setLoading(true);
    try {
      const params = isSpare ? { spare_asset_id: asset.id } : { to_asset_id: asset.id };
      const { data } = await api.get("/spare-issues", { params });
      setIssues(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Load candidate machines for the issue picker (only when this is a spare)
    if (isSpare) {
      api.get("/assets").then(({ data }) => {
        // Exclude other spares and this spare itself
        setAssets(data.filter((a) => a.category !== "Attachments and Jigs" && a.id !== asset.id));
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [asset?.id]);

  const activeIssue = issues.find((i) => i.status === "active");

  // For non-spare assets: only render if there's actual spare-issue history to show. Reduces noise.
  if (!isSpare && !loading && issues.length === 0) return null;

  return (
    <Card data-testid="spare-issue-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4 text-[#7C1D3A]" />
          {isSpare ? "Deployment & Issue History" : "Spares Issued to This Asset"}
        </CardTitle>
        {isSpare && canIssueSpare && !activeIssue && (
          <Button onClick={() => setIssueOpen(true)} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" size="sm" data-testid="issue-spare-btn">
            <Send className="w-4 h-4 mr-2" /> Issue to Machine
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {isSpare && activeIssue && (
          <div className="bg-emerald-50 border-b border-emerald-200 p-4 flex items-start justify-between flex-wrap gap-3">
            <div className="text-sm">
              <div className="font-semibold text-emerald-800">
                Currently {activeIssue.issue_type === "returnable" ? "issued (returnable)" : "permanently deployed"} to {activeIssue.to_asset_snapshot?.name}
              </div>
              <div className="text-xs text-emerald-700 mt-1 font-mono">{activeIssue.to_asset_snapshot?.asset_id}{(activeIssue.issued_line || activeIssue.to_asset_snapshot?.location) && <span className="font-sans"> · Line: {activeIssue.issued_line || activeIssue.to_asset_snapshot?.location}</span>}</div>
              <div className="text-xs text-emerald-700 mt-1">
                Doc: <span className="font-mono font-semibold">{activeIssue.doc_number}</span> · Issued {formatDate(activeIssue.issued_date)} by {activeIssue.issued_by_name}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadPdf(activeIssue.id, activeIssue.doc_number)} data-testid="download-issue-doc">
                <FileDown className="w-3.5 h-3.5 mr-1" /> Issue Doc
              </Button>
              {activeIssue.issue_type === "returnable" && canIssueSpare && (
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setReturnIssue(activeIssue)} data-testid="return-spare-btn">
                  <PackageCheck className="w-3.5 h-3.5 mr-1" /> Mark Returned
                </Button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
        ) : issues.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            {isSpare ? "This spare has not been issued to any machine yet." : "No spares currently issued to this asset."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Doc #</th>
                <th className="px-4 py-3">Type</th>
                {isSpare ? (
                  <th className="px-4 py-3">To Machine</th>
                ) : (
                  <th className="px-4 py-3">Spare</th>
                )}
                <th className="px-4 py-3">Issued</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {issues.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#7C1D3A]">{i.doc_number}</td>
                  <td className="px-4 py-3 text-xs">
                    <Badge variant="outline" className={i.issue_type === "returnable" ? "border-blue-300 text-blue-700 bg-blue-50" : "border-indigo-300 text-indigo-700 bg-indigo-50"}>
                      {i.issue_type === "returnable" ? "Returnable" : "Permanent"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {isSpare ? (
                      <div className="font-medium text-slate-900">{i.to_asset_snapshot?.name} <span className="text-xs text-slate-500 ml-2 font-mono">{i.to_asset_snapshot?.asset_id}</span>
                        {(i.issued_line || i.to_asset_snapshot?.location) && <div className="text-[11px] text-slate-500 font-normal">Line: {i.issued_line || i.to_asset_snapshot?.location}</div>}
                        {i.fits_compatibility === false && <div className="text-[11px] text-amber-700 font-normal" data-testid={`issue-outside-fit-${i.doc_number}`}>Issued outside fits list</div>}
                      </div>
                    ) : (
                      <div className="font-medium text-slate-900">{i.spare_asset_snapshot?.name} <span className="text-xs text-slate-500 ml-2 font-mono">{i.spare_asset_snapshot?.asset_id}</span></div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(i.issued_date)}<div className="text-slate-400">{i.issued_by_name}</div></td>
                  <td className="px-4 py-3">
                    <Badge className={
                      i.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      i.status === "returned" ? "bg-slate-100 text-slate-600 border-slate-200" :
                      "bg-indigo-50 text-indigo-700 border-indigo-200"
                    }>
                      {i.status.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => downloadPdf(i.id, i.doc_number)} className="text-[#7C1D3A] hover:underline text-xs">PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      {issueOpen && (
        <IssueSpareDialog
          open={issueOpen}
          onOpenChange={setIssueOpen}
          spare={asset}
          assets={assets}
          onDone={() => { setIssueOpen(false); load(); onChanged && onChanged(); }}
        />
      )}
      {returnIssue && (
        <ReturnSpareDialog
          issue={returnIssue}
          onClose={() => setReturnIssue(null)}
          onDone={() => { setReturnIssue(null); load(); onChanged && onChanged(); }}
        />
      )}
    </Card>
  );
}

const downloadPdf = async (issueId, docNumber) => {
  try {
    const res = await api.get(`/spare-issues/${issueId}/document.pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(docNumber || "spare-issue").replace(/\//g, "_")}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch { toast.error("Failed to download issue document"); }
};

function IssueSpareDialog({ open, onOpenChange, spare, assets, onDone }) {
  const [form, setForm] = useState({
    to_asset_id: "",
    issue_type: spare.is_returnable === false ? "permanent" : "returnable",
    expected_return_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const compatIds = spare.compatible_asset_ids || [];
  const compatTypes = (spare.compatible_machine_types || []).map((t) => t.trim().toLowerCase());
  const hasCompat = compatIds.length > 0 || compatTypes.length > 0;
  const fitIds = new Set(assets.filter((a) => compatIds.includes(a.id) || compatTypes.includes((a.name || "").trim().toLowerCase())).map((a) => a.id));
  const selected = assets.find((a) => a.id === form.to_asset_id);

  const submit = async () => {
    if (!form.to_asset_id) { toast.error("Please select the machine"); return; }
    setSubmitting(true);
    try {
      await api.post("/spare-issues", {
        spare_asset_id: spare.id,
        to_asset_id: form.to_asset_id,
        issue_type: form.issue_type,
        expected_return_date: form.expected_return_date || null,
        notes: form.notes || null,
      });
      toast.success("Spare issued & document generated");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Issue failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Issue Spare to Machine</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-[#fcf0f3] border border-[#f5c4d3] rounded-md p-3">
            <div className="text-xs text-[#7C1D3A] font-semibold uppercase tracking-wider">Spare</div>
            <div className="font-heading text-lg font-semibold text-slate-900">{spare.name}</div>
            <div className="text-xs text-slate-500 font-mono">{spare.asset_id}</div>
          </div>
          <div>
            <Label>Issue to Machine *</Label>
            <SingleMachinePicker
              assets={assets}
              value={form.to_asset_id}
              onChange={(v) => setForm({ ...form, to_asset_id: v })}
              fitIds={hasCompat ? fitIds : null}
              testid="issue-to-machine"
            />
            {selected && (
              <div className="text-xs text-slate-600 mt-1.5" data-testid="issue-line-info">
                Line / Location: <b>{selected.location || "— not set on machine —"}</b>
              </div>
            )}
            {selected && hasCompat && !fitIds.has(selected.id) && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5" data-testid="issue-fit-warning">
                This machine is not in the spare's "fits" list. You can still issue it — it will be flagged on the record.
              </div>
            )}
          </div>
          <div>
            <Label>Issue Type *</Label>
            <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
              <SelectTrigger data-testid="issue-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="returnable">Returnable</SelectItem>
                <SelectItem value="permanent">Permanent Deployment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.issue_type === "returnable" && (
            <div><Label>Expected Return Date</Label><Input type="date" value={form.expected_return_date} onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} /></div>
          )}
          <div><Label>Purpose / Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Replacement for worn-out spindle bearing" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="confirm-issue-btn">
            {submitting ? "Issuing..." : "Issue & Generate Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnSpareDialog({ issue, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/spare-issues/${issue.id}/return`, { return_notes: notes || null });
      toast.success("Spare returned to inventory");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Return failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Mark Spare as Returned</DialogTitle></DialogHeader>
        <div className="text-sm space-y-3">
          <div className="text-slate-600">
            Confirm return of spare <b>{issue.spare_asset_snapshot?.name}</b> from machine <b>{issue.to_asset_snapshot?.name}</b>.
          </div>
          <div><Label>Return notes (condition, etc.)</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700">
            {busy ? "Processing..." : "Confirm Return"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
